/**
 * Prospecting service — the firm-wide "are we bringing in new work" signal, distinct from working
 * leads/opportunities already in the system. Backed by the same Airtable Interactions table
 * (services/interactions.js) — a prospecting touch is simply an Interaction logged with neither a
 * Lead nor a Project link. No new table, no per-rep ownership (the team works a shared pool).
 */
import { getProspectingActivity, logInteraction } from './interactions.js'

/** Plain constant, not a settings UI — adjust directly if 10/week isn't the right target. */
export const WEEKLY_PROSPECTING_TARGET = 10

function startOfWeek(d) {
  // Monday-start week, local time.
  const day = d.getDay() // 0 = Sunday
  const diff = (day === 0 ? -6 : 1) - day
  const monday = new Date(d)
  monday.setHours(0, 0, 0, 0)
  monday.setDate(monday.getDate() + diff)
  return monday
}

/**
 * This week's prospecting count + pace. `elapsedFraction` is how far through the work-week
 * (Mon-Sun) today falls, so callers can flag "behind pace" before the week is even over rather
 * than only at the end of the week.
 */
export async function getWeeklyProspectingStatus() {
  const now = new Date()
  const monday = startOfWeek(now)
  const activity = await getProspectingActivity({ sinceISO: monday.toISOString() })
  const count = activity.length
  const elapsedFraction = Math.min(1, ((now - monday) / 86400000 + 1) / 7)
  const paceTarget = WEEKLY_PROSPECTING_TARGET * elapsedFraction
  const status = count >= WEEKLY_PROSPECTING_TARGET ? 'ahead' : count >= paceTarget ? 'onpace' : 'behind'
  return { count, target: WEEKLY_PROSPECTING_TARGET, elapsedFraction, status, weekStart: monday.toISOString() }
}

/** Log a bare prospecting touch — thin wrapper enforcing no Lead/Project link. */
export function logProspectingActivity(input = {}) {
  const { leadId, projectId, ...rest } = input
  return logInteraction(rest)
}
